import { z } from "zod";
import type { NotificationPreferences } from "@praesto/shared-types";

export const RoleEnum = z.enum(["tenant_owner", "manager_hotel", "manager_functional", "staff"]);
export type Role = z.infer<typeof RoleEnum>;

// Mirror of the `NotificationPreferences` shared-type (packages/shared-types).
// Kept structurally equal so `UpdateUserSchema`'s inferred field is assignable
// from the shared-type with NO cast at the call site, while still giving the
// server real runtime validation instead of `z.record(z.unknown())`.
//
// ChannelId = "sms" | "whatsapp" | "push" | "email" | "inApp"
const ChannelIdEnum = z.enum(["sms", "whatsapp", "push", "email", "inApp"]);

// NotificationType — full string union from shared-types.
const NotificationTypeEnum = z.enum([
  "shift.assigned",
  "shift.changed",
  "shift.cancelled",
  "checklist.reminder",
  "sop.updated",
  "auth.mfa_code",
  "system.alert",
  "report.created",
  "report.resolved",
  "shift.starting_soon",
  "schedule.published",
  "shift.swapped",
  "leave.requested",
  "leave.decided",
  "availability.reminder",
  "sop.published",
  "monitoring.comment.mentioned",
]);

// `channels` is `Readonly<Record<ChannelId, boolean>>` — every channel required,
// so we model each key explicitly (a `z.record` would infer a Partial). `.readonly()`
// mirrors the `Readonly<...>` wrapper in the shared-type.
const ChannelsSchema = z
  .object({
    sms: z.boolean(),
    whatsapp: z.boolean(),
    push: z.boolean(),
    email: z.boolean(),
    inApp: z.boolean(),
  })
  .readonly();

// `perType` is `Partial<Record<NotificationType, ReadonlyArray<ChannelId>>>` —
// a user overrides the handful of types they care about, never all of them.
//
// `partialRecord`, not `record`: in zod 4 an enum-keyed `z.record` became
// EXHAUSTIVE. It infers a complete `Record<K, V>` and, at runtime, rejects any
// object missing a key — which is every object this schema will ever see. The
// compile-time guard below is what caught it during the zod 3 → 4 bump.
//
// The inner `.readonly()` makes each value `readonly ChannelId[]` so the call
// site's `ReadonlyArray<ChannelId>` values are accepted (a mutable array param
// would reject them).
const PerTypeSchema = z.partialRecord(NotificationTypeEnum, z.array(ChannelIdEnum).readonly());

const QuietHoursSchema = z
  .object({
    start: z.string(),
    end: z.string(),
    timezone: z.string(),
  })
  .readonly();

export const NotificationPreferencesSchema = z
  .object({
    channels: ChannelsSchema,
    perType: PerTypeSchema,
    // `quietHours?` is optional in the shared-type (exactOptionalPropertyTypes).
    quietHours: QuietHoursSchema.optional(),
  })
  .readonly();

// Compile-time guard: the `NotificationPreferences` shared-type must be
// assignable to the inferred schema type. This is the load-bearing direction —
// it is exactly what lets the call site pass a `NotificationPreferences` value
// to `updateUser` with NO cast. (The reverse direction is intentionally not
// asserted: zod's `.optional()` widens `quietHours?` to `T | undefined`, which
// `exactOptionalPropertyTypes` forbids on the shared-type's optional property —
// a benign asymmetry that never reaches the wire, since the value is one a
// caller already produced as a valid `NotificationPreferences`.)
type _SharedTypeAssignableToInfer =
  NotificationPreferences extends z.infer<typeof NotificationPreferencesSchema> ? true : never;
const _prefsTypeCheck: _SharedTypeAssignableToInfer = true;
void _prefsTypeCheck;

// Normalized to lowercase at the schema boundary so the stored `users.email`
// always matches the lowercased query used by the email-first HR resolver
// (`functions/src/integrations/appliers/resolve-employee.ts`), which also
// lowercases the incoming HR email before querying. Both sides of the join
// must agree on casing.
const Email = z
  .string()
  .email()
  .transform((v) => v.toLowerCase());
const StrongPassword = z
  .string()
  .min(12)
  .regex(/[A-Za-z]/)
  .regex(/[0-9]/);

export const InviteUserSchema = z.object({
  email: Email,
  role: RoleEnum,
  hotelId: z.string().min(1),
  departments: z.array(z.string().min(1)).default([]),
});
export type InviteUserInput = z.infer<typeof InviteUserSchema>;

export const CreateUserSchema = z.object({
  email: Email,
  password: StrongPassword,
  displayName: z.string().min(1).max(120),
  role: RoleEnum,
  hotelId: z.string().min(1),
  departments: z.array(z.string().min(1)).default([]),
});
export type CreateUserInput = z.infer<typeof CreateUserSchema>;

export const UpdateUserSchema = z.object({
  displayName: z.string().min(1).max(120).optional(),
  status: z.enum(["pending", "active", "disabled"]).optional(),
  // `null` removes the stored number; an absent key leaves it alone (C-230).
  phone: z
    .string()
    .regex(/^\+\d{8,15}$/)
    .nullable()
    .optional(),
  photoUrl: z.string().url().optional(),
  // `null` removes the stored date, like the phone above (C-233).
  birthDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  employment: z
    .object({
      ccnlLevel: z.string().max(20).optional(),
      contractType: z
        .enum(["permanent", "fixed_term", "apprentice", "seasonal", "intern"])
        .optional(),
      hoursPerWeek: z.number().min(1).max(80).optional(),
      startDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
      endDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    })
    .optional(),
  skills: z.array(z.string().min(1)).max(50).optional(),
  // `null` removes the stored note, like the phone above (C-234).
  notes: z.string().max(2000).nullable().optional(),
  vacationBalance: z
    .object({
      year: z.number().int().min(2024).max(2100),
      totalDays: z.number().min(0).max(60),
    })
    .optional(),
  notificationPreferences: NotificationPreferencesSchema.optional(),
  locale: z.enum(["it", "en"]).optional(),
});
export type UpdateUserInput = z.infer<typeof UpdateUserSchema>;

export const GrantMembershipSchema = z.object({
  hotelId: z.string().min(1),
  role: RoleEnum,
  departments: z.array(z.string().min(1)).default([]),
});
export type GrantMembershipInput = z.infer<typeof GrantMembershipSchema>;

export const LeaveTypeSchema = z.enum(["vacation", "permit", "sick", "leave"]);

export const CreateLeaveSchema = z.object({
  userId: z.string().min(1),
  hotelId: z.string().min(1),
  type: LeaveTypeSchema,
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().max(500).optional(),
});
export type CreateLeaveInput = z.infer<typeof CreateLeaveSchema>;

export const RejectLeaveSchema = z.object({
  rejectionReason: z.string().min(1).max(500),
});
export type RejectLeaveInput = z.infer<typeof RejectLeaveSchema>;

export const AvailabilityRuleTypeSchema = z.enum(["unavailable", "preferred_off", "preferred_on"]);

export const CreateAvailabilityRuleSchema = z
  .object({
    userId: z.string().min(1),
    type: AvailabilityRuleTypeSchema,
    dayOfWeek: z.number().int().min(0).max(6).optional(),
    slot: z.enum(["M", "P", "N"]).optional(),
    validFrom: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    validTo: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    reason: z.string().max(200).optional(),
  })
  .refine((d) => d.dayOfWeek !== undefined || d.slot !== undefined, { message: "rule_too_broad" });
export type CreateAvailabilityRuleInput = z.infer<typeof CreateAvailabilityRuleSchema>;

export const CustomSkillSchema = z.object({
  id: z
    .string()
    .regex(/^[a-z0-9-]+$/)
    .min(1)
    .max(64),
  label: z.string().min(1).max(120),
  group: z.enum(["operative", "language", "certification", "custom"]),
});
export type CustomSkillInput = z.infer<typeof CustomSkillSchema>;

/**
 * Pure: would removing/demoting `target.userId` (or revoking their
 * tenant_owner role) leave the tenant with zero owners? Used both by
 * the server-side guard (disable / role change / membership revoke)
 * and by the UI to preemptively disable destructive buttons.
 */
export function isLastOwner(
  memberships: ReadonlyArray<{ userId: string; role: Role }>,
  target: { userId: string },
): boolean {
  const owners = memberships.filter((m) => m.role === "tenant_owner");
  if (owners.length === 0) return false;
  if (owners.length >= 2) return false;
  return owners[0]!.userId === target.userId;
}
